/**
 * HistoryView — past tickets list rendered inside the ChatWidget panel.
 *
 *  - Fetches via fetchMyTickets() on mount.
 *  - List-only for v1; ticket rows are clickable but currently a no-op
 *    (parent may choose to show a toast or stub).
 */
import React, { useEffect, useState } from "react";
import { fetchMyTickets } from "./chatApi";

const STATUS_COLORS = {
  New: "#d93025",
  Open: "#d93025",
  "In Progress": "#e0a800",
  InProgress: "#e0a800",
  Resolved: "#1e7d3a",
  Closed: "#1e7d3a",
};

function statusDot(status) {
  const color = STATUS_COLORS[status] || "#888";
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
      }}
    />
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;
  const week = 7 * day;

  if (diff < min) return "just now";
  if (diff < hr) return `${Math.floor(diff / min)} min ago`;
  if (diff < day) return `${Math.floor(diff / hr)} hours ago`;
  if (diff < week) return `${Math.floor(diff / day)} days ago`;
  return `${Math.floor(diff / week)} weeks ago`;
}

export default function HistoryView({ onBack, onSelectTicket }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchMyTickets();
        if (cancelled) return;
        setTickets(Array.isArray(res?.tickets) ? res.tickets : []);
      } catch (_err) {
        if (!cancelled) setError("Couldn't load tickets. Try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={styles.wrap}>
      <div style={styles.title}>Past tickets</div>

      <div style={styles.list}>
        {loading && <div style={styles.empty}>Loading…</div>}
        {!loading && error && <div style={styles.empty}>{error}</div>}
        {!loading && !error && tickets.length === 0 && (
          <div style={styles.empty}>No tickets yet.</div>
        )}
        {!loading &&
          !error &&
          tickets.map((t) => (
            <button
              key={t.ticket_id}
              type="button"
              onClick={() => onSelectTicket && onSelectTicket(t)}
              style={styles.row}
            >
              <div style={styles.rowTop}>
                {statusDot(t.status)}
                <span style={styles.sev}>{t.severity || "P?"}</span>
                <span style={styles.sep}>·</span>
                <span style={styles.status}>{t.status || "Unknown"}</span>
                <span style={styles.sep}>·</span>
                <span style={styles.titleText}>
                  {t.title || t.site_url || t.site_name || "Untitled"}
                </span>
              </div>
              <div style={styles.rowBottom}>{formatRelative(t.created_at)}</div>
            </button>
          ))}
      </div>

      <button type="button" onClick={onBack} style={styles.backBtn}>
        ← Back to chat
      </button>
    </div>
  );
}

const styles = {
  wrap: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    background: "#fafafa",
    overflow: "hidden",
  },
  title: {
    padding: "12px 14px 6px",
    fontSize: 13,
    fontWeight: 600,
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: "0 8px",
  },
  empty: {
    color: "#888",
    fontSize: 13,
    textAlign: "center",
    padding: 24,
  },
  row: {
    width: "100%",
    textAlign: "left",
    background: "white",
    border: "1px solid #eee",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    color: "#222",
    flexWrap: "wrap",
  },
  sev: { fontWeight: 600 },
  status: { color: "#555" },
  sep: { color: "#bbb", margin: "0 2px" },
  titleText: {
    color: "#222",
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowBottom: {
    marginTop: 4,
    fontSize: 11,
    color: "#888",
  },
  backBtn: {
    margin: 10,
    background: "white",
    color: "#135fc2",
    border: "1px solid #135fc2",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
};
